// "Working as" (Shipping hub, Phase 1, 2026-10-27): who is doing the work at a shop-floor screen.
//
// jobq-api POST /auth/operator-session turns a typed PIN into a 16-digit token that every ordinary PIN
// check accepts (the face sign-in token's table, source 'pin'); a face match's token IS a session too.
// The server ends it 10 minutes ABSOLUTE after sign-in ("Switch person" signs in again). This tab keeps
// {token, user_id, name, role, expires_at, source} in sessionStorage so each action sends the token
// instead of asking for a PIN again.
//
// Rules:
//   * a session is live until 30 s before its expiry (an action never goes out with a token about to die);
//   * nothing here reads or writes the admin session (sessionStorage adminPin / userRole): a PC left in an
//     admin session must never stand in for the operator;
//   * no config.js import (pinPrompt's tests load this module without mocking axios).

export const SESSION_KEY = 'jobq.operatorSession';
export const LIVE_MARGIN_MS = 30 * 1000;
export const SESSION_EVENT = 'jobq-operator-session';

function storage() {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch (_) {
    return null;
  }
}

function announce() {
  try {
    window.dispatchEvent(new Event(SESSION_EVENT));
  } catch (_) {
    // no window (never in the app)
  }
}

/** A stored session, shaped and checked, or null. */
export function normalizeSession(s) {
  if (!s || typeof s !== 'object') return null;
  const token = s.token != null ? String(s.token).trim() : '';
  const exp = Date.parse(s.expires_at);
  if (!token || !Number.isFinite(exp)) return null;
  return {
    token,
    user_id: s.user_id ?? null,
    name: s.name ? String(s.name) : null,
    role: s.role ? String(s.role) : null,
    source: s.source === 'face' ? 'face' : 'pin',
    expires_at: new Date(exp).toISOString(),
  };
}

export function readSession() {
  const st = storage();
  if (!st) return null;
  try {
    return normalizeSession(JSON.parse(st.getItem(SESSION_KEY) || 'null'));
  } catch (_) {
    return null;
  }
}

export function writeSession(s) {
  const st = storage();
  const n = normalizeSession(s);
  if (!st || !n) return null;
  try {
    st.setItem(SESSION_KEY, JSON.stringify(n));
  } catch (_) {
    return null;
  }
  announce();
  return n;
}

export function clearSession() {
  const st = storage();
  if (st) {
    try { st.removeItem(SESSION_KEY); } catch (_) { /* nothing to clear */ }
  }
  announce();
}

/** Live = has a token and more than LIVE_MARGIN_MS left. */
export function isLive(s, now = Date.now()) {
  const n = normalizeSession(s);
  if (!n) return false;
  return Date.parse(n.expires_at) - now > LIVE_MARGIN_MS;
}

/** The live session, or null (an expired one is cleared on the way). */
export function liveSession(now = Date.now()) {
  const s = readSession();
  if (!s) return null;
  if (isLive(s, now)) return s;
  clearSession();
  return null;
}

/** Whole minutes left, rounded up ("signs out in 7 min"); 0 when not live. */
export function minutesLeft(s, now = Date.now()) {
  if (!isLive(s, now)) return 0;
  const ms = Date.parse(normalizeSession(s).expires_at) - now;
  return Math.max(1, Math.ceil(ms / 60000));
}

/** Call `fn` whenever this tab's session changes (sign in, switch, sign out). Returns the unsubscribe. */
export function onSessionChange(fn) {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(SESSION_EVENT, fn);
  return () => window.removeEventListener(SESSION_EVENT, fn);
}
